import { Injectable, inject } from '@angular/core';

import { LocalStorageService } from '../../storage/local-storage.service';

/**
 * localStorage key for this browser's device id (single-active-session enforcement, see
 * docs/plans/2026-07-29-single-active-session-design.md). Deliberately `localStorage`, not
 * `sessionStorage`: it must be shared across every tab of the SAME browser, so opening a
 * second tab of your own session never looks like "another device" to the backend — only a
 * genuinely different browser/profile/machine mints its own id and takes over.
 */
const DEVICE_ID_STORAGE_KEY = 'cv.deviceId';

/**
 * localStorage key recording whether THIS device has ever successfully claimed the active
 * slot. Deliberately persisted, not an in-memory flag: an in-memory "pending" flag resets to
 * true on every reload, which sounds harmless but isn't — it means ANY reload, including of a
 * device that was just superseded, would silently re-send `X-Register-Device` and steal the
 * slot back with no real login having happened. Two real devices alternately hitting refresh
 * would then ping-pong the active slot back and forth forever. Persisting this instead means a
 * reload only re-registers when this browser has genuinely never claimed the slot before —
 * once claimed, a reload is just a reload; a superseded device stays superseded until an
 * actual logout (see `clearRegistration`) gives it a legitimate reason to reclaim.
 */
const DEVICE_REGISTERED_STORAGE_KEY = 'cv.deviceRegistered';

/**
 * Header name for the per-browser device id. Lives here (not in the interceptor) so any
 * caller that must bypass `HttpClient` and attach the header by hand — e.g. `BulkLogService`'s
 * unload-safe `fetch(keepalive)` beacon — can import it without an import cycle: the
 * interceptor pulls in `SessionSupersededService` -> `LogoutService` ->
 * `UsageLoggingService` -> `BulkLogService`.
 */
export const DEVICE_ID_HEADER = 'X-Device-Id';

/**
 * Header that tells horizon-service "register this device as active" (see
 * `ActiveDeviceEnforcementFilter`). Sent on exactly the first horizon-service call of a fresh
 * session, whichever endpoint that happens to be — see `consumeRegistrationPending`.
 */
export const REGISTER_DEVICE_HEADER = 'X-Register-Device';

export const IS_PROJECTION_APP_HEADER = 'X-Is-Projection-App';

/**
 * Mints and holds the per-browser device id sent as `X-Device-Id` on horizon-service calls.
 * The id identifies a browser, not a user or a request — it carries no PII and is never used
 * for authentication, only as the tiebreaker the backend uses to decide which browser is this
 * account's current active login.
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdentityService {
  private readonly localStorage = inject(LocalStorageService);
  private readonly id: string;

  constructor() {
    const existing = this.localStorage.get(DEVICE_ID_STORAGE_KEY);
    if (existing) {
      this.id = existing;
    } else {
      this.id = crypto.randomUUID();
      this.localStorage.set(DEVICE_ID_STORAGE_KEY, this.id);
    }
  }

  deviceId(): string {
    return this.id;
  }

  /**
   * True at most once per browser, ever — returns whether THIS call should carry
   * `X-Register-Device`, i.e. whether this browser has never yet claimed the active-device
   * slot. Immediately records the claim in localStorage so no later call, including after a
   * reload, does this again — that persistence is the whole point (see
   * `DEVICE_REGISTERED_STORAGE_KEY`'s doc comment). The one legitimate way to get a fresh
   * chance to reclaim is an actual logout (`clearRegistration`).
   */
  consumeRegistrationPending(): boolean {
    if (this.localStorage.get(DEVICE_REGISTERED_STORAGE_KEY) === 'true') return false;
    this.localStorage.set(DEVICE_REGISTERED_STORAGE_KEY, 'true');
    return true;
  }

  /**
   * Called by `LogoutService` on every logout: an explicit logout is the one signal that a
   * later login on this browser (by this user or a different one sharing the machine) is a
   * genuine new session that should be allowed to reclaim the active-device slot, as opposed
   * to a routine reload of a session that's already established.
   */
  clearRegistration(): void {
    this.localStorage.remove(DEVICE_REGISTERED_STORAGE_KEY);
  }
}
